import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, User, Loader2 } from "lucide-react";
import { Reveal } from "@/components/shared/Reveal";
import { CTAButton } from "@/components/shared/CTAButton";
import { getBlogPost } from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    getBlogPost(slug)
      .then(setPost)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="flex justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  }

  if (notFound || !post) {
    return (
      <div className="container-x py-40 text-center" data-testid="blog-notfound">
        <h1 className="font-heading font-bold text-3xl text-ink">Article not found</h1>
        <CTAButton to="/blog" className="mt-6 inline-flex">Back to Blog</CTAButton>
      </div>
    );
  }

  return (
    <article data-testid="blog-post-page">
      {/* Hero */}
      <header className="relative bg-brand text-white overflow-hidden">
        <div className="grain absolute inset-0" />
        <img src={post.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand via-brand/85 to-brand/60" />
        <div className="container-x relative py-20 lg:py-28 max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6" data-testid="back-to-blog">
            <ArrowLeft className="h-4 w-4" /> All Articles
          </Link>
          <span className="inline-block rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-brand-sky">{post.category}</span>
          <h1 className="mt-5 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl leading-[1.05]">{post.title}</h1>
          <div className="mt-6 flex items-center gap-6 text-sm text-white/70">
            <span className="flex items-center gap-2"><User className="h-4 w-4" />{post.author}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{post.read_time}</span>
            <span>{post.date}</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <section className="bg-white py-16 lg:py-24">
        <div className="container-x max-w-3xl">
          <Reveal className="rounded-3xl overflow-hidden shadow-card mb-12">
            <img src={post.image} alt={post.title} className="w-full h-72 sm:h-96 object-cover" />
          </Reveal>
          <div className="prose-custom space-y-6 text-lg leading-relaxed text-ink/80">
            {post.content.split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <div className="mt-14 rounded-3xl bg-surface border border-gray-100 p-8 sm:p-10 text-center">
            <h3 className="font-heading font-bold text-2xl text-ink">Planning a move?</h3>
            <p className="mt-2 text-muted-foreground">Get a free, no-obligation quote from our specialists today.</p>
            <CTAButton to="/#quote" className="mt-6 inline-flex">Get a Quote</CTAButton>
          </div>
        </div>
      </section>
    </article>
  );
}
